import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ChapterInfo } from './useReadingPath';

interface ReadingProgress {
  storyId: string;
  currentNodeId: string;
  nodeHistory: string[];
  historyIndex: number;
  lastReadAt: string;
}

interface ReaderState {
  // Current reading session
  currentStoryId: string | null;
  currentNodeId: string | null;
  nodeHistory: string[];
  // Current position within nodeHistory (allows moving back/forward without truncating)
  historyIndex: number;

  // Chapter path from server (for ToC display)
  chapterPath: ChapterInfo[];

  // Persisted progress for all stories
  progress: Record<string, ReadingProgress>;

  // Actions
  startReading: (storyId: string, rootNodeId: string) => void;
  navigateToNode: (nodeId: string) => void;
  goBack: () => string | null;
  clearCurrentSession: () => void;
  getProgress: (storyId: string) => ReadingProgress | undefined;
  setChapterPath: (path: ChapterInfo[]) => void;
  jumpToChapter: (index: number) => string | null;
  clearAllProgress: () => void;
}

export const useReaderStore = create<ReaderState>()(
  persist(
    (set, get) => ({
      currentStoryId: null,
      currentNodeId: null,
      nodeHistory: [],
      historyIndex: -1,
      chapterPath: [],
      progress: {},

      startReading: (storyId: string, rootNodeId: string) => {
        const existingProgress = get().progress[storyId];

        if (existingProgress) {
          // Resume from saved progress at the saved position
          // Use saved historyIndex if available, otherwise default to end of history
          const savedIndex =
            existingProgress.historyIndex ?? existingProgress.nodeHistory.length - 1;
          // Clamp to valid bounds in case history was modified
          const validIndex = Math.max(
            0,
            Math.min(savedIndex, existingProgress.nodeHistory.length - 1)
          );
          set({
            currentStoryId: storyId,
            currentNodeId: existingProgress.currentNodeId,
            nodeHistory: existingProgress.nodeHistory,
            historyIndex: validIndex,
          });
        } else {
          // Start fresh
          set({
            currentStoryId: storyId,
            currentNodeId: rootNodeId,
            nodeHistory: [rootNodeId],
            historyIndex: 0,
          });
        }
      },

      navigateToNode: (nodeId: string) => {
        const { currentStoryId, nodeHistory, historyIndex } = get();
        if (!currentStoryId) return;

        // Check if this node is already the next one in history (re-tracing same path)
        const nextInHistory = nodeHistory[historyIndex + 1];
        if (nextInHistory === nodeId) {
          // Move forward in history without modifying the history array, but persist progress
          const newIndex = historyIndex + 1;
          const progress: ReadingProgress = {
            storyId: currentStoryId,
            currentNodeId: nodeId,
            nodeHistory,
            historyIndex: newIndex,
            lastReadAt: new Date().toISOString(),
          };

          set((state) => ({
            currentNodeId: nodeId,
            historyIndex: newIndex,
            progress: {
              ...state.progress,
              [currentStoryId]: progress,
            },
          }));
          return;
        }

        // New choice - truncate history from current position and append
        const newHistory = [...nodeHistory.slice(0, historyIndex + 1), nodeId];
        const newIndex = newHistory.length - 1;

        // Save progress
        const progress: ReadingProgress = {
          storyId: currentStoryId,
          currentNodeId: nodeId,
          nodeHistory: newHistory,
          historyIndex: newIndex,
          lastReadAt: new Date().toISOString(),
        };

        set((state) => ({
          currentNodeId: nodeId,
          nodeHistory: newHistory,
          historyIndex: newIndex,
          progress: {
            ...state.progress,
            [currentStoryId]: progress,
          },
        }));
      },

      goBack: () => {
        const { currentStoryId, nodeHistory, historyIndex } = get();
        if (!currentStoryId || historyIndex <= 0) return null;

        // Move back in history without truncating
        const newIndex = historyIndex - 1;
        const previousNodeId = nodeHistory[newIndex];

        set((state) => ({
          currentNodeId: previousNodeId,
          historyIndex: newIndex,
          progress: {
            ...state.progress,
            [currentStoryId]: {
              storyId: currentStoryId,
              currentNodeId: previousNodeId,
              nodeHistory,
              historyIndex: newIndex,
              lastReadAt: new Date().toISOString(),
            },
          },
        }));

        return previousNodeId;
      },

      clearCurrentSession: () => {
        set({
          currentStoryId: null,
          currentNodeId: null,
          nodeHistory: [],
          historyIndex: -1,
          chapterPath: [],
        });
      },

      getProgress: (storyId: string) => {
        return get().progress[storyId];
      },

      setChapterPath: (path: ChapterInfo[]) => {
        set({ chapterPath: path });
      },

      jumpToChapter: (index: number) => {
        const { currentStoryId, chapterPath, nodeHistory } = get();
        // Validate index is within bounds of chapterPath
        if (index < 0 || index >= chapterPath.length) {
          return null;
        }

        const targetNodeId = chapterPath[index].nodeId;

        // Clamp historyIndex to valid nodeHistory bounds to prevent undefined access
        // This handles cases where chapterPath (from server) may be longer than local nodeHistory
        const safeIndex = Math.min(index, nodeHistory.length - 1);

        // Update current position and persist progress
        set((state) => ({
          currentNodeId: targetNodeId,
          historyIndex: safeIndex,
          ...(currentStoryId
            ? {
                progress: {
                  ...state.progress,
                  [currentStoryId]: {
                    storyId: currentStoryId,
                    currentNodeId: targetNodeId,
                    nodeHistory,
                    historyIndex: safeIndex,
                    lastReadAt: new Date().toISOString(),
                  },
                },
              }
            : {}),
        }));

        return targetNodeId;
      },

      clearAllProgress: () => {
        set({
          currentStoryId: null,
          currentNodeId: null,
          nodeHistory: [],
          historyIndex: -1,
          chapterPath: [],
          progress: {},
        });
      },
    }),
    {
      name: 'yarntales-reader-progress',
      partialize: (state) => ({ progress: state.progress }),
    }
  )
);
